import { Container } from 'pixi.js';
import type { SceneContext } from '../types/game.types';

export abstract class BaseScene extends Container {
  protected context: SceneContext;
  protected isLoaded: boolean = false;

  constructor(context: SceneContext) {
    super();
    this.context = context;
  }

  /**
   * Load scene assets and prepare resources.
   * Called once before the scene is first entered.
   */
  abstract load(): Promise<void>;

  /**
   * Called when the scene becomes active.
   * Set up event listeners and initialize scene state.
   */
  abstract enter(): void;

  /**
   * Called every frame with delta time in seconds.
   * Update game logic and animations.
   */
  abstract update(delta: number): void;

  /**
   * Called when the scene becomes inactive.
   * Clean up event listeners but keep resources loaded.
   */
  abstract exit(): void;

  /**
   * Called when the scene is permanently removed.
   * Clean up all resources and dispose textures.
   */
  abstract unload(): void;

  /**
   * Handle window resize events.
   * Override this to adjust scene layout.
   */
  resize(width: number, height: number): void {
    this.context.width = width;
    this.context.height = height;
  }

  /**
   * Check if the scene has been loaded.
   */
  getIsLoaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Mark the scene as loaded.
   */
  protected setLoaded(loaded: boolean): void {
    this.isLoaded = loaded;
  }
}
